import {
  GitHubRoutingPermissions,
  type StoredGitHubRoutingPermissionType,
} from "@afar-research/client-runtime/connection";
import type { EnvironmentId } from "@afar-research/contracts";
import * as Effect from "effect/Effect";
import * as Layer from "effect/Layer";
import * as Queue from "effect/Queue";
import * as Stream from "effect/Stream";

import {
  getClientSettingsSnapshot,
  persistClientSettings,
  subscribeClientSettings,
} from "../clientSettingsStore";

/**
 * GitHub sharing grants, read from this client's settings.
 *
 * The settings store is the one place these live; this only adapts it to the shape the connection
 * runtime asks for, so a grant changed in Settings is in force for the next pull request read
 * without a reload.
 */
export const gitHubRoutingPermissionsLayer = Layer.succeed(
  GitHubRoutingPermissions,
  GitHubRoutingPermissions.of({
    current: Effect.sync(
      () =>
        getClientSettingsSnapshot()
          .gitHubRoutingPermissions as ReadonlyArray<StoredGitHubRoutingPermissionType>,
    ),
    changes: Stream.callback<ReadonlyArray<StoredGitHubRoutingPermissionType>>((queue) =>
      Effect.acquireRelease(
        Effect.sync(() =>
          subscribeClientSettings(() => {
            Queue.offerUnsafe(
              queue,
              getClientSettingsSnapshot()
                .gitHubRoutingPermissions as ReadonlyArray<StoredGitHubRoutingPermissionType>,
            );
          }),
        ),
        (unsubscribe) => Effect.sync(unsubscribe),
      ),
    ),
    set: (input) =>
      Effect.sync(() => {
        const settings = getClientSettingsSnapshot();
        const others = settings.gitHubRoutingPermissions.filter(
          (entry) => entry.environmentId !== input.environmentId,
        );
        persistClientSettings({
          ...settings,
          gitHubRoutingPermissions:
            input.permission === "off"
              ? others
              : [
                  ...others,
                  {
                    environmentId: input.environmentId,
                    connectionKey: input.connectionKey,
                    permission: input.permission,
                  },
                ],
        });
      }),
    forget: (environmentId: EnvironmentId) =>
      Effect.sync(() => {
        const settings = getClientSettingsSnapshot();
        persistClientSettings({
          ...settings,
          gitHubRoutingPermissions: settings.gitHubRoutingPermissions.filter(
            (entry) => entry.environmentId !== environmentId,
          ),
        });
      }),
  }),
);
